/**
 * Native Push Notification Service
 * Handles iOS/Android native notifications via Capacitor
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import api from './api';

class PushNotificationService {
  constructor() {
    this.isInitialized = false;
    this.deviceToken = null;
    this.initPromise = null;
  }

  /**
   * Initialize Push Notifications
   * Call this after user login
   */
  async initialize() {
    // Only run on native platforms (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      console.log('PushNotifications: Not a native platform, skipping initialization');
      return;
    }

    if (this.isInitialized) {
      console.log('PushNotifications: Already initialized');
      return;
    }

    // App.js ruft initialize() beim Start von zwei Stellen fast gleichzeitig auf
    // (localStorage-Pfad und user-Effekt). isInitialized wird erst nach zwei awaits
    // gesetzt, also kamen beide Aufrufe durch den Guard: jeder hängte vier Listener
    // an und schickte den Token mehrfach ans Backend, und ein Tipp auf die
    // Mitteilung löste die Navigation mehrfach aus. Läuft schon eine
    // Initialisierung, teilen sich alle Aufrufer dasselbe Promise.
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._initialize().finally(() => {
      this.initPromise = null;
    });
    return this.initPromise;
  }

  async _initialize() {
    try {
      // IMPORTANT: Set up listeners BEFORE calling register()
      // Otherwise the registration event can be missed

      // Listen for registration success
      PushNotifications.addListener('registration', async (token) => {
        console.log('PushNotifications: Device token received:', token.value);
        this.deviceToken = token.value;

        // Send token to backend
        await this.sendTokenToBackend(token.value);
      });

      // Listen for registration errors
      // Capacitor liefert { error: string } (z. B. fehlendes aps-environment,
      // FCM SERVICE_NOT_AVAILABLE). Den Grund als Text loggen, sonst steht in
      // Sentry nur "[object keys: error]" und die Ursache bleibt unsichtbar.
      PushNotifications.addListener('registrationError', (error) => {
        console.error('PushNotifications: Registration error:', error?.error || error);
      });

      // Listen for push notifications received while app is in foreground
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('PushNotifications: Foreground notification:', notification);
      });

      // Listen for push notification actions (user tapped notification)
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('PushNotifications: User tapped notification:', action);
        const data = action.notification.data;

        // Handle navigation based on notification type
        this.handleNotificationTap(data);
      });

      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      console.log('PushNotifications: Permission result:', permResult.receive);

      if (permResult.receive === 'granted') {
        // Register with Apple/Google push servers
        await PushNotifications.register();
        console.log('PushNotifications: Registration initiated');
      } else {
        console.warn('PushNotifications: Permission denied');
      }

      this.isInitialized = true;
      console.log('PushNotifications: Initialization complete');

    } catch (error) {
      console.error('PushNotifications: Initialization failed:', error);
    }
  }

  /**
   * Send device token to backend
   * Backend stores token for sending push notifications
   */
  async sendTokenToBackend(token) {
    // Das 'registration'-Event kommt asynchron vom Betriebssystem, auch nach einem
    // Logout. Ohne Anmeldung kann das Backend das Gerät keinem Konto zuordnen (401);
    // nach Logout/Sitzungsende räumt cleanup() auf, der nächste Login registriert neu.
    if (!localStorage.getItem('token')) {
      console.log('PushNotifications: Kein Login, Geräte-Token wird nicht gesendet');
      return;
    }

    try {
      const platform = Capacitor.getPlatform(); // 'ios' or 'android'

      await api.post('/notifications/register-device', {
        token,
        platform
      });

      console.log('PushNotifications: Token sent to backend');
    } catch (error) {
      console.error('PushNotifications: Failed to send token to backend:', error);
    }
  }

  /**
   * Beim Logout: Device-Token im Backend deaktivieren, sonst bekommt der nächste
   * Nutzer auf demselben Gerät (Schul-iPad!) die Pushes des vorigen Kontos.
   * Muss laufen, solange der Auth-Token noch gültig ist — also VOR clearAuthStorage().
   */
  async unregisterFromBackend(authToken) {
    // Ohne Auth-Token endet der Request garantiert mit 401. Das Gerät bleibt dann dem
    // Konto zugeordnet, bis sich jemand anderes darauf anmeldet: registerDeviceToken im
    // Backend deaktiviert dabei alle fremden Zuordnungen desselben Geräte-Tokens.
    if (!Capacitor.isNativePlatform() || !this.deviceToken || !authToken) return;

    try {
      // Auth-Header explizit mitgeben: der Aufrufer (logout) räumt localStorage
      // synchron auf, der Request-Interceptor fände das Token sonst nicht mehr.
      const headers = { Authorization: `Bearer ${authToken}` };
      await api.post('/notifications/unregister-device', { token: this.deviceToken }, { headers });
      console.log('PushNotifications: Token im Backend deaktiviert');
    } catch (error) {
      console.warn('PushNotifications: Token konnte nicht deaktiviert werden:', error?.message);
    }
  }

  /**
   * Handle notification tap - navigate to relevant screen
   */
  handleNotificationTap(data) {
    // Use router navigation based on notification type
    const { type, actionUrl, chatId, sessionId } = data;

    // This will be handled by the app's navigation system
    // Emit custom event that app can listen to
    window.dispatchEvent(new CustomEvent('notification-tap', {
      detail: { type, actionUrl, chatId, sessionId }
    }));
  }

  /**
   * Get current badge count
   */
  async getBadgeCount() {
    if (!Capacitor.isNativePlatform()) return 0;

    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications.length;
    } catch (error) {
      console.error('PushNotifications: Failed to get badge count:', error);
      return 0;
    }
  }

  /**
   * Clear all delivered notifications
   */
  async clearNotifications() {
    if (!Capacitor.isNativePlatform()) return;
    if (!this.isInitialized) return;

    try {
      await PushNotifications.removeAllDeliveredNotifications();
      console.log('PushNotifications: Cleared all notifications');
    } catch (error) {
      console.error('PushNotifications: Failed to clear notifications:', error);
    }
  }

  /**
   * Cleanup - remove all listeners
   */
  async cleanup() {
    if (!Capacitor.isNativePlatform()) return;

    await PushNotifications.removeAllListeners();
    this.isInitialized = false;
    this.deviceToken = null;
    console.log('PushNotifications: Cleanup complete');
  }
}

// Export singleton instance
export default new PushNotificationService();
